import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "./api";
import { setJwt, clearAuthStorage } from "./session";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [capabilities, setCapabilities] = useState({});
  const [loading, setLoading] = useState(true);

  const loadCapabilities = useCallback(async () => {
    try {
      const { data } = await api.get("/me/capabilities");
      setCapabilities(data?.capabilities || {});
      return data?.capabilities || {};
    } catch (e) {
      setCapabilities({});
      return {};
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadCapabilities();
      return data;
    } catch (e) {
      // 401 / network failures: ensure local state is fully cleared so a stale
      // token cannot leave the UI in a half-authenticated zombie state.
      if (e?.response?.status === 401) clearAuthStorage();
      setUser(null);
      setCapabilities({});
      return null;
    }
  }, [loadCapabilities]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    // Defensive: scrub any leftover auth state from a previous user/browser
    // BEFORE writing the new token. This prevents the "stale token from a
    // different account" bug on shared devices.
    clearAuthStorage();
    const { data } = await api.post("/auth/login", { email, password });
    setJwt(data.token);
    setUser(data.user);
    await loadCapabilities();
    return data.user;
  }, [loadCapabilities]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout endpoint failure is non-blocking — we still clear local state.
      // eslint-disable-next-line no-console
      console.warn("Logout request failed:", err?.response?.status || err?.message);
    }
    clearAuthStorage();
    setUser(null);
    setCapabilities({});
    window.location.href = "/login";
  }, []);

  const hasCapability = useCallback(
    (module, capability) => !!(capabilities?.[module]?.[capability]),
    [capabilities]
  );

  const value = useMemo(
    () => ({ user, setUser, loading, login, logout, refresh, capabilities, hasCapability }),
    [user, loading, login, logout, refresh, capabilities, hasCapability]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
